import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface TestRecord {
  id: string;
  deviceId: string;
  testType: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  startTime: string;
  endTime?: string;
  duration?: number;
  result?: any;
  error?: string;
  source?: 'tests' | 'post-release';
}

const TestHistory: React.FC = () => {
  const { t } = useTranslation();
  const [tests, setTests] = useState<TestRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState<'all'|'tests'|'post-release'>('all');
  // limit 调整为 200，移除日期范围筛选
  const [devices, setDevices] = useState<{ id: string; name?: string }[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailText, setDetailText] = useState('');

  const loadTestHistory = async () => {
    try {
      // 仅在非 all 时拼接筛选参数，避免把 "all" 当成有效过滤值
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '200');
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (deviceFilter !== 'all') params.set('deviceId', deviceFilter);
      if (sourceFilter !== 'all') params.set('source', sourceFilter);
      // 防缓存
      params.set('_t', String(Date.now()));
      const url = `${API_ENDPOINTS.TESTS}/history?${params.toString()}`;
      const response = await fetch(url, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } });
      const data = await response.json();
      setTests(data.tests || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('Failed to load test history:', error);
    }
  };

  useEffect(() => {
    loadTestHistory();
  }, [page, statusFilter, deviceFilter, sourceFilter]);

  // 当筛选条件变化时，重置为第一页并清空历史，避免重复
  useEffect(() => {
    setPage(1);
  }, [statusFilter, deviceFilter, sourceFilter]);

  // 🎯 从URL参数中读取runId，如果存在则自动打开详情
  useEffect(() => {
    try {
      const hash = window.location.hash; // HashRouter模式
      const queryStart = hash.indexOf('?');
      if (queryStart !== -1) {
        const queryString = hash.substring(queryStart + 1);
        const params = new URLSearchParams(queryString);
        const runId = params.get('runId');
        if (runId) {
          // 自动打开该run的详情
          handleView({ id: runId, source: 'post-release' } as TestRecord);
          // 清理URL中的runId参数，避免重复打开
          const hashPath = hash.split('?')[0];
          window.history.replaceState({}, '', window.location.pathname + hashPath);
        }
      }
    } catch (err) {
      console.error('解析runId参数失败:', err);
    }
  }, []); // 只在组件挂载时执行一次

  // 读取实际设备列表，填充 Device 下拉
  useEffect(() => {
    const loadDevices = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.DEVICES);
        const list = await res.json();
        const ds = Array.isArray(list) ? list.map((d: any) => ({ id: d.id, name: d.name })) : [];
        setDevices(ds);
      } catch (e) {
        console.error('Failed to load devices:', e);
        setDevices([]);
      }
    };
    loadDevices();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'warning';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const formatDuration = (value?: number) => {
    if (!value && value !== 0) return '-';
    // 后端有的以毫秒写入，有的以秒写入，做个兼容
    let totalSeconds = Number(value);
    if (totalSeconds > 10000) totalSeconds = Math.round(totalSeconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('zh-CN');
  };

  const handleView = async (rec: TestRecord) => {
    try {
      setDetailTitle(`Details - ${rec.id}`);
      let text = '';
      if (rec.source === 'post-release') {
        const res = await fetch(`${API_ENDPOINTS.API_BASE}/api/post-release/run/${rec.id}`);
        const data = await res.json();
        text = JSON.stringify(data, null, 2);
      } else {
        const res = await fetch(`${API_ENDPOINTS.TESTS}/${rec.id}`);
        const data = await res.json();
        text = JSON.stringify(data, null, 2);
      }
      setDetailText(text);
      setDetailOpen(true);
    } catch (e) {
      setDetailText(String(e));
      setDetailOpen(true);
    }
  };

  const handleDownload = async (rec: TestRecord) => {
    try {
      if (rec.source === 'post-release') {
        // 直接下载服务器拼好的文本日志
        const link = document.createElement('a');
        link.href = `${API_ENDPOINTS.API_BASE}/api/post-release/run/${rec.id}/logs.txt`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const res = await fetch(`${API_ENDPOINTS.TESTS}/${rec.id}/logs`);
        const logs = await res.json();
        const content = Array.isArray(logs)
          ? logs.map((lg: any) => `[${lg.timestamp || ''}] ${lg.level || ''}: ${lg.message || ''}`).join('\n')
          : JSON.stringify(logs, null, 2);
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `test-${rec.id}-logs.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Download logs failed:', e);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('testHistory.title')}
      </Typography>

      {/* 筛选器 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('testHistory.filters')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('testHistory.filters')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('testHistory.all')}</MenuItem>
                  <MenuItem value="completed">{t('testHistory.completed')}</MenuItem>
                  <MenuItem value="failed">{t('testHistory.failed')}</MenuItem>
                  <MenuItem value="running">{t('testHistory.running')}</MenuItem>
                  <MenuItem value="cancelled">{t('testHistory.cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  label="Source"
                  onChange={(e) => setSourceFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="tests">Tests</MenuItem>
                  <MenuItem value="post-release">Post-Release</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('testHistory.device')}</InputLabel>
                <Select
                  value={deviceFilter}
                  label={t('testHistory.device')}
                  onChange={(e) => setDeviceFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('testHistory.allDevices')}</MenuItem>
                  {devices.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name || d.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <IconButton onClick={loadTestHistory} color="primary">
                <RefreshIcon />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 测试记录表格 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('testHistory.testId')}</TableCell>
                <TableCell>{t('testHistory.device')}</TableCell>
                <TableCell>{t('testHistory.testType')}</TableCell>
                <TableCell>{t('testHistory.status')}</TableCell>
                <TableCell>{t('testHistory.startTime')}</TableCell>
                <TableCell>{t('testHistory.endTime')}</TableCell>
                <TableCell>{t('testHistory.duration')}</TableCell>
                <TableCell>{t('testHistory.operation')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map((test) => (
                <TableRow key={test.id}>
                  <TableCell>{test.id}</TableCell>
                  <TableCell>{test.deviceId}</TableCell>
                  <TableCell>{test.testType}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`testHistory.${test.status}`) || test.status}
                      color={getStatusColor(test.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(test.startTime)}</TableCell>
                  <TableCell>
                    {test.endTime 
                      ? formatDateTime(test.endTime) 
                      : (test.status === 'running' ? '-' : test.source === 'post-release' ? '-' : '-')}
                  </TableCell>
                  <TableCell>
                    {test.duration !== undefined && test.duration !== null
                      ? formatDuration(test.duration)
                      : test.endTime && test.startTime
                      ? formatDuration((new Date(test.endTime).getTime() - new Date(test.startTime).getTime()) / 1000)
                      : test.status === 'running' && test.startTime
                      ? formatDuration((Date.now() - new Date(test.startTime).getTime()) / 1000)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" title="查看详情" onClick={() => handleView(test)}>
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" title="下载日志" onClick={() => handleDownload(test)}>
                      <DownloadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 分页 */}
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      </Paper>
      {/* 详情弹窗 */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{detailTitle}</DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{detailText}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestHistory;
